import { z } from 'zod';
/**
 * Geofences, and the first thing that ever reads their geometry.
 *
 * `Geofence.geometry` has been a JSONB column since the intelligence engine was
 * built, and nothing in the platform has ever looked inside it: events name a
 * geofence by id and name, policies match on `correlation.geofenceId`, and the
 * shape itself sat unread. A map is its first consumer, which means these
 * schemas describe a column nothing has been validating — the repo seed writes a
 * circle and `engine.int.test.ts` writes `{}`.
 *
 * **OIP does not compute containment, and drawing a zone must not imply it
 * does.** Whether a vehicle was inside a geofence is asserted by the PROVIDER,
 * through `GEOFENCE_EXIT` events carrying `geofenceId`. Nothing here fits a
 * position to a shape, and a reader must not be led to read a pin near a circle
 * as a platform finding.
 */
export const geoPointSchema = z.object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
});
export const circleGeometrySchema = z.object({
    type: z.literal('circle'),
    center: geoPointSchema,
    /** Metres. Leaflet draws circles in metres, so no conversion is implied. */
    radiusM: z.number().positive(),
});
export const polygonGeometrySchema = z.object({
    type: z.literal('polygon'),
    /**
     * Three or more points. The ring is CLOSED by the renderer, not by the data —
     * a stored closing point identical to the first is accepted and harmless, and
     * requiring one would reject every polygon drawn by a tool that omits it.
     */
    points: z.array(geoPointSchema).min(3),
});
export const geofenceGeometrySchema = z.discriminatedUnion('type', [
    circleGeometrySchema,
    polygonGeometrySchema,
]);
export const geofenceDtoSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    /** RESTRICTED | ZONE, as stored. A free string: nothing enforces a vocabulary. */
    kind: z.string(),
    /**
     * Null when the stored shape is not one this version can draw.
     *
     * The row is still LISTED. A geofence policies match on and events name is
     * real whether or not its shape can be rendered, and hiding it would make an
     * unreadable geometry look like a missing geofence — the harder of the two to
     * diagnose. Visible and unplottable beats invisible.
     */
    geometry: geofenceGeometrySchema.nullable(),
    /**
     * TENANT | PROVIDER — who created it.
     *
     * A PROVIDER zone was auto-provisioned from an inbound event: the provider
     * asserted a vehicle left it, and OIP holds a name and nothing else. It has NO
     * geometry by construction, which is a different fact from a shape that could
     * not be read, and the two must not be presented alike.
     */
    source: z.enum(['TENANT', 'PROVIDER']),
    /** The provider's own id for the zone, when one was supplied. */
    externalId: z.string().nullable(),
    /**
     * The provider's own word for the zone's type, verbatim.
     *
     * Present even when OIP did not recognise it — which is the case worth
     * showing, because an unrecognised type leaves the zone unclassified and would
     * otherwise look identical to a provider that sends no type at all.
     */
    providerKind: z.string().nullable(),
    /** When an inbound event last named it. Null for a zone nothing references. */
    lastSeenAt: z.string().nullable(),
    createdAt: z.string(),
});
/**
 * The vocabulary a zone may be classified into.
 *
 * ⚠️ **`DEPOT` is a CLASSIFICATION, not a new entity (D5).** A depot is a place
 * the tenant already has a geofence for; giving it its own table would mean two
 * records for one place, and the one a provider syncs would not be the one a
 * vehicle is based at.
 *
 * ⚠️ **A DEPOT exit must NOT become a `COMPLIANCE_RISK`.** `RESTRICTED` is what
 * makes an exit a compliance matter; a vehicle leaving its own depot is a
 * vehicle starting work. The correlation rules key on `RESTRICTED` and nothing
 * else, so this is already true — and it is the first consequence D5 names,
 * which is why it is recorded here rather than left to be rediscovered.
 */
export const GEOFENCE_KINDS = ['RESTRICTED', 'ZONE', 'DEPOT', 'UNCLASSIFIED'];
export const geofenceKindSchema = z.enum(GEOFENCE_KINDS);
/**
 * Classifying a zone.
 *
 * `RESTRICTED` is what makes an exit a COMPLIANCE_RISK rather than the neutral
 * default. It changes what FUTURE exits produce and never rewrites an incident
 * already recorded — an incident states what the platform believed at the time,
 * and reclassifying history would make the trail disagree with the decisions
 * taken from it.
 */
export const classifyGeofenceSchema = z.object({ kind: geofenceKindSchema }).strict();
export const geofenceListResponseSchema = z.object({
    items: z.array(geofenceDtoSchema),
    /**
     * How many of `items` carry a shape this version could not read.
     *
     * Counted rather than inferred by the client: the reason a geometry is null
     * belongs to whoever parsed it, and a client recounting nulls would be
     * repeating a decision the server already made.
     */
    unreadable: z.number().int(),
    /**
     * Zones auto-provisioned from an event and not yet classified.
     *
     * Surfaced as its own number because it is the actionable one: until somebody
     * says a zone is RESTRICTED, exits from it are raised at the neutral
     * classification. Folding these into `unreadable` would report a zone awaiting
     * a decision as a data fault.
     */
    unclassified: z.number().int(),
});
// ── where the fleet is ──────────────────────────────────────────────────────
/**
 * How far back a "last known position" may be and still be shown.
 *
 * A pin is read as WHERE THE VEHICLE IS. Beyond a day that is no longer true of
 * a moving fleet, and a stale pin is worse than an absent one because nothing on
 * a map says how old it is. Vehicles outside the window are counted, not drawn.
 */
export const FLEET_POSITION_WINDOW_HOURS = 24;
export const fleetPositionSchema = z.object({
    vehicleId: z.string().uuid(),
    registration: z.string(),
    name: z.string().nullable(),
    latitude: z.number(),
    longitude: z.number(),
    /** When the position was reported. A pin without this cannot be judged. */
    at: z.string(),
    speedKph: z.number().nullable(),
});
/**
 * Where the fleet is, as far as anything reported.
 *
 * The counts are the honest part. A map showing 6 pins for a fleet of 85 looks
 * like the fleet, and the only thing that stops it being read that way is saying
 * how many are missing and why — a vehicle that has not reported a position in
 * the window is a different problem from one that never reports any.
 */
export const fleetPositionsResponseSchema = z.object({
    positions: z.array(fleetPositionSchema),
    /** Active, unarchived vehicles in the tenant — the denominator for the map. */
    activeVehicles: z.number().int(),
    windowHours: z.number().int(),
    asOf: z.string(),
});
//# sourceMappingURL=geofence.js.map